/**
 * What a name, a password and an address have to look like.
 *
 * Pure, and in core, for the usual reason: both ends judge these and they
 * have to agree. A screen that accepts a password the server will refuse
 * sends somebody round a loop with no explanation at the end of it, and a
 * screen that refuses one the server would take is a screen that lies.
 *
 * Nothing here hashes anything. Hashing is the server's and only the
 * server's - a password the browser hashed is a password, and the hash is
 * then the secret.
 */

/** The longest a name may be. It is drawn over somebody's head. */
export const NAME_MAX = 24;
/**
 * The shortest a password may be.
 *
 * Eight. Long enough that guessing is not a pastime, short enough that
 * somebody typing it at a kitchen table does not give up. This is a game
 * about burning a fortress, not a bank.
 */
export const PASSWORD_MIN = 8;

/** Why a name cannot be used, as a key the interface can translate. */
export type NameProblem = "name-empty" | "name-long" | "name-odd";

/**
 * A name has to be a name.
 *
 * Letters, digits, spaces and the marks people actually put in names - an
 * apostrophe for Ukrainian, a hyphen for a double-barrelled one. No control
 * characters: a name is drawn on screen and carried in messages, and nothing
 * good comes of a newline in either.
 */
export function nameProblem(raw: string): NameProblem | null {
  const name = raw.trim();
  if (name.length === 0) return "name-empty";
  if (name.length > NAME_MAX) return "name-long";
  for (const ch of name) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) return "name-odd";
  }
  return null;
}

export type PasswordProblem = "pass-short" | "pass-long";

/**
 * A password has to be long enough, and not absurd.
 *
 * A ceiling as well as a floor: scrypt is deliberately slow, and a password
 * of a megabyte is a way to make this process think about one person for a
 * very long time.
 */
export function passwordProblem(password: string): PasswordProblem | null {
  if (password.length < PASSWORD_MIN) return "pass-short";
  if (password.length > 200) return "pass-long";
  return null;
}

/**
 * Does that look like an address at all?
 *
 * Deliberately loose. The only thing this can honestly check is that there is
 * something, an @, and something with a dot in it - whether anybody reads
 * what is sent there is a question only sending can answer, and the answer
 * comes back as a bounce rather than as a regular expression. A stricter rule
 * here turns away real addresses to no purpose.
 */
export function emailLooksReal(email: string): boolean {
  const at = email.trim();
  if (at.length === 0 || at.length > 254) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(at);
}

/**
 * How long a reset ticket is good for.
 *
 * An hour. Long enough to find the message in a spam folder, short enough
 * that a link left in an inbox for a year is not a key to the account.
 */
export const TICKET_MINUTES = 60;
